import { FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { isNotEmpty } from '../../../../../_dashboard/helpers'
import { CmsService } from '../core/_models'
import clsx from 'clsx'
import { useListView } from '../core/ListViewProvider'
import { DataLoading } from '../../../../../_dashboard/partials/components/loading/DataLoading'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { createOne, getAll, getOneByIdbylang, updateOne } from '../../../../services/common_requests'
import { useIntl } from 'react-intl'
import { useQueryRequest } from '../core/QueryRequestProvider'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

type Props = {
  isCmsServiceLoading: boolean
  CmsService: CmsService
}

const editCmsServiceSchema = Yup.object().shape({
  translations: Yup.array().of(
    Yup.object().shape({
      title: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(100, 'Maximum 100 symbols')
        .required('Title is required'),
      description: Yup.string().max(500, 'Maximum 500 symbols'),
    })
  ),
})

const CmsServiceEditModalForm: FC<Props> = ({ CmsService, isCmsServiceLoading }) => {
  const { itemIdForUpdate, setItemIdForUpdate } = useListView()
  const { refetchData } = useQueryResponse()
  const { state } = useQueryRequest();
  const [query] = useState<any>(state);
  const intl = useIntl()
  const [CmsServiceForEdit] = useState<CmsService>({
    ...CmsService,
    translations: CmsService.translations ?? [{ language: 'en', title: '', description: '' }],
    isActive: CmsService.isActive ?? true,
    order: CmsService.order ?? 0,
  })
  const LANGUAGE_NAMES: { [key: string]: string } = {
    en: intl.formatMessage({ id: 'COMMON.ENGLISH_LANGUAGE' }),
    fr: intl.formatMessage({ id: 'COMMON.FRENSH_LANGUAGE' }),
    es: intl.formatMessage({ id: 'COMMON.ESPANGOL_LANGUAGE' }),
    ar: intl.formatMessage({ id: 'COMMON.ARABIC_LANGUAGE' }),
  };

  const [languages, setLanguages] = useState<{ code: string; name: string }[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState('fr');

  const mergeTranslationsForLanguages = (
    languageCodes: string[],
    existing: CmsService['translations'] = [],
  ) =>
    languageCodes.map((code) => {
      const found = existing.find((t) => t.language === code);
      return found || { language: code, title: '', description: '' };
    });

  useEffect(() => {
    getAll(query, '/languages').then((data) => {
      if (data && Array.isArray(data)) {
        const formattedLanguages = data.map((code: string) => ({
          code,
          name: LANGUAGE_NAMES[code] || code.toUpperCase(),
        }));
        setLanguages(formattedLanguages);
      }
    });
  }, [query]);

  const [uploadedImage, setUploadedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetchData()
    }
    setItemIdForUpdate(undefined)
  }

  const handleImageChange = (file: File | null) => {
    if (file) {
      setUploadedImage(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const formik = useFormik({
    initialValues: CmsServiceForEdit,
    validationSchema: editCmsServiceSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(itemIdForUpdate)) {
          const selectedTranslation = values.translations.find(t => t.language === selectedLanguage);

          if (!selectedTranslation || !selectedTranslation._id) {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND' }));
            return;
          }

          await updateOne(
            {
              title: selectedTranslation.title,
              description: selectedTranslation.description || '',
            },
            `/cms-services/translation/${selectedTranslation._id}`,
          );

          const formData = new FormData();
          formData.append('title', values.translations[0].title);
          formData.append('isActive', String(values.isActive));
          formData.append('order', String(values.order ?? 0));
          if (uploadedImage) {
            formData.append('image', uploadedImage);
          }

          const response = await updateOne(formData, `/cms-services/${itemIdForUpdate}`);
          if (response?.payload?.code === 2021) {
            toast.success(intl.formatMessage({ id: 'ADMIN.COMMON.SUCCESS.OPTION_UPDATED_SUCCESSFULLY' }), {
              position: 'top-right',
              autoClose: 4000,
            });
            cancel(true);
          } else {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_OPTION' }), {
              position: 'top-right',
              autoClose: 4000,
            });
          }
        } else {
          if (!uploadedImage) {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.IMAGE_REQUIRED' }, { defaultMessage: 'Image is required' }));
            return;
          }

          const formData = new FormData();
          formData.append('image', uploadedImage);
          formData.append('title', values.translations[0].title);
          formData.append('translations', JSON.stringify(values.translations.map(translation => ({
            language: translation.language || 'en',
            title: translation.title,
            description: translation.description || '',
          }))));
          formData.append('isActive', String(values.isActive));
          formData.append('order', String(values.order ?? 0));

          const response = await createOne(formData, '/cms-services');
          if (response?.payload?.code === 2021) {
            toast.success(intl.formatMessage({ id: 'ADMIN.COMMON.SUCCESS.OPTION_ADDED_SUCCESSFULLY' }), {
              position: 'top-right',
              autoClose: 4000,
            });
            cancel(true);
          } else if (response?.payload?.code === 4400) {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.NAME_ALREADY_EXIST' }), {
              position: 'top-right',
              autoClose: 4000,
            });
          } else {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_OPTION' }), {
              position: 'top-right',
              autoClose: 4000,
            });
          }
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const updateTranslationField = (field: 'title' | 'description', value: string) => {
    const translations = [...formik.values.translations];
    let index = translations.findIndex((t) => t.language === selectedLanguage);

    if (index === -1) {
      translations.push({ language: selectedLanguage, title: '', description: '' });
      index = translations.length - 1;
    }

    translations[index] = { ...translations[index], [field]: value };
    formik.setFieldValue('translations', translations);
  };

  useEffect(() => {
    if (languages.length === 0) {
      return;
    }

    const merged = mergeTranslationsForLanguages(
      languages.map((lang) => lang.code),
      formik.values.translations,
    );
    formik.setFieldValue('translations', merged);

    if (!languages.some((lang) => lang.code === selectedLanguage)) {
      setSelectedLanguage(languages[0].code);
    }
  }, [languages]);

  useEffect(() => {
    if (!itemIdForUpdate || languages.length === 0) {
      return;
    }

    getOneByIdbylang(`/cms-services/${itemIdForUpdate}?language=all`)
      .then((data) => {
        if (data) {
          const mergedTranslations = mergeTranslationsForLanguages(
            languages.map((lang) => lang.code),
            data.translations || [],
          );
          formik.setValues({
            ...formik.values,
            translations: mergedTranslations.map((t: any) => ({
              ...t,
              _id: t._id,
            })),
            isActive: data.isActive ?? true,
            order: data.order ?? 0,
            image: data.image,
          });
        }
      })
      .catch((error) => console.error('Error fetching translation:', error));
  }, [itemIdForUpdate, languages]);

  const selectedTranslation = formik.values.translations.find(t => t.language === selectedLanguage);
  const currentImageUrl = imagePreview || (formik.values.image?.url
    ? (process.env.REACT_APP_API_URL_IMAGE || '') + formik.values.image.url
    : null);

  return (
    <>
      <form
        id='kt_modal_add_cms-service_form'
        className='form'
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <div
          className='d-flex flex-column scroll-y me-n15 pe-6'
          id='kt_modal_add_cms-service_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_cms-service_header'
          data-kt-scroll-wrappers='#kt_modal_add_CmsService_scroll'
          data-kt-scroll-offset='300px'
        >
          <div className='fv-row mb-4'>
            <label className='d-block fw-bold fs-6 mb-5'>
              {intl.formatMessage({ id: 'COLUMNS.IMAGE' })}
            </label>
            {!itemIdForUpdate ? (
              <div>
                <input
                  type='file'
                  name='image'
                  accept='.png, .jpg, .jpeg, .webp'
                  className='form-control'
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleImageChange(e.target.files[0])
                    }
                  }}
                />
                {currentImageUrl && (
                  <div
                    className='mt-3 image-input-wrapper w-125px h-125px'
                    style={{ backgroundImage: `url('${currentImageUrl}')`, backgroundSize: 'cover' }}
                  />
                )}
              </div>
            ) : (
              <div
                className='image-input image-input-outline'
                style={{
                  backgroundImage: currentImageUrl ? `url('${currentImageUrl}')` : undefined,
                }}
              >
                <div className='image-input-wrapper w-125px h-125px'></div>
                <label className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'>
                  <i className='bi bi-pencil-fill fs-7'></i>
                  <input
                    type='file'
                    name='image'
                    accept='.png, .jpg, .jpeg, .webp'
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleImageChange(e.target.files[0])
                      }
                    }}
                  />
                </label>
              </div>
            )}
          </div>

          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.LANGUAGE' })}
            </label>
            <select
              className='form-select'
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.NAME' })}
            </label>
            <input
              placeholder={intl.formatMessage({ id: 'COLUMNS.NAME' })}
              value={selectedTranslation?.title || ''}
              onChange={(e) => updateTranslationField('title', e.target.value)}
              type='text'
              name={`title-${selectedLanguage}`}
              className='form-control'
              autoComplete='off'
              disabled={formik.isSubmitting || isCmsServiceLoading}
            />
          </div>

          <div className='fv-row mb-4'>
            <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.DESCRIPTION' })}
            </label>
            <textarea
              className='form-control'
              rows={4}
              name={`description-${selectedLanguage}`}
              value={selectedTranslation?.description || ''}
              onChange={(e) => updateTranslationField('description', e.target.value)}
              disabled={formik.isSubmitting || isCmsServiceLoading}
            />
          </div>

          <div className='fv-row mb-4'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'COLUMNS.ORDER' })}</label>
            <input
              type='number'
              className='form-control'
              value={formik.values.order ?? 0}
              onChange={(e) => formik.setFieldValue('order', Number(e.target.value))}
            />
          </div>

          <div className='form-check form-switch mb-4'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={formik.values.isActive}
              onChange={() => formik.setFieldValue('isActive', !formik.values.isActive)}
            />
            <label className='form-check-label'>{intl.formatMessage({ id: 'COLUMNS.STATUS' })}</label>
          </div>
        </div>

        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            disabled={formik.isSubmitting || isCmsServiceLoading}
          >
            {intl.formatMessage({ id: 'HEADER.RESET' })}
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            disabled={isCmsServiceLoading || formik.isSubmitting}
          >
            <span className='indicator-label'>{intl.formatMessage({ id: 'HEADER.SAVE' })}</span>
            {(formik.isSubmitting || isCmsServiceLoading) && (
              <span className='indicator-progress'>
                {intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {(formik.isSubmitting || isCmsServiceLoading) && <DataLoading />}
    </>
  )
}

export { CmsServiceEditModalForm }
